import { useEffect, useMemo, useRef, useState } from 'react';
import { Extension } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import Image from '@tiptap/extension-image';
import { Box, Divider, IconButton, Stack, Tooltip } from '@mui/material';
import { cleanOfficeHtml } from '@shared/richtext/officePaste';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  Image as ImageIcon,
  FormatQuote,
  FormatIndentIncrease,
  FormatIndentDecrease,
  Undo,
  Redo,
  TextFields,
  FormatClear,
  ZoomIn,
  ZoomOut,
  PhotoSizeSelectLarge,
} from '@mui/icons-material';

const SizedImage = Image.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width'),
        renderHTML: (attributes) => (attributes.width ? { width: attributes.width } : {}),
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute('height'),
        renderHTML: (attributes) => (attributes.height ? { height: attributes.height } : {}),
      },
    };
  },
});

const FontFamily = Extension.create({
  name: 'fontFamily',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (element) => element.style.fontFamily || null,
            renderHTML: (attributes) => {
              if (!attributes.fontFamily) return {};
              return { style: `font-family: ${attributes.fontFamily}` };
            },
          },
        },
      },
    ];
  },
});

const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
});

const BackgroundColor = Extension.create({
  name: 'backgroundColor',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          backgroundColor: {
            default: null,
            parseHTML: (element) => element.style.backgroundColor || null,
            renderHTML: (attributes) => {
              if (!attributes.backgroundColor) return {};
              return { style: `background-color: ${attributes.backgroundColor}` };
            },
          },
        },
      },
    ];
  },
});

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  minHeight?: number;
  maxBodyHeight?: number | string;
}

export default function RichTextEditor({
  value,
  onChange,
  disabled = false,
  placeholder = 'Type your message here...',
  minHeight = 320,
  maxBodyHeight = 'min(52vh, 420px)',
}: RichTextEditorProps) {
  const [showTextTools, setShowTextTools] = useState(false);
  const [preferredTextColor, setPreferredTextColor] = useState('#000000');
  const [preferredBackgroundColor, setPreferredBackgroundColor] = useState('#fff59d');
  const onChangeRef = useRef(onChange);
  const textColorInputRef = useRef<HTMLInputElement | null>(null);
  const bgColorInputRef = useRef<HTMLInputElement | null>(null);
  const editorFrameRef = useRef<HTMLDivElement | null>(null);
  const activeImageRef = useRef<HTMLImageElement | null>(null);
  const dragWidthRef = useRef<number | null>(null);
  const dragStateRef = useRef<{ startX: number; startWidth: number; dragging: boolean }>({
    startX: 0,
    startWidth: 0,
    dragging: false,
  });
  const [imageHandle, setImageHandle] = useState<{ visible: boolean; left: number; top: number }>({
    visible: false,
    left: 0,
    top: 0,
  });
  const textPalette = ['#000000', '#1f2937', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0ea5e9', '#2563eb', '#7c3aed', '#be185d'];
  const highlightPalette = ['#fff59d', '#fef08a', '#fde68a', '#fecaca', '#fed7aa', '#bfdbfe', '#bae6fd', '#bbf7d0', '#ddd6fe', '#fbcfe8'];
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        link: false,
        underline: false,
        heading: {
          levels: [2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        protocols: ['http', 'https', 'mailto'],
      }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      BackgroundColor,
      Underline,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      SizedImage.configure({
        allowBase64: true,
      }),
    ],
    []
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const editor = useEditor({
    extensions,
    content: value || '',
    immediatelyRender: false,
    editable: !disabled,
    editorProps: {
      transformPastedHTML: cleanOfficeHtml,
      attributes: {
        class: 'tiptap-editor-content',
      },
    },
    onUpdate: ({ editor: tiptapEditor }) => {
      onChangeRef.current(tiptapEditor.getHTML());
    },
  }, [extensions]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    editor.view.dom.setAttribute('data-placeholder', placeholder);
  }, [editor, placeholder]);

  useEffect(() => {
    if (!editor) return;
    const incoming = value || '';
    if (incoming !== editor.getHTML()) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [editor, value]);

  const setLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href ?? '';
    const url = window.prompt('Enter URL', previousUrl);
    if (url === null) return;
    if (url.trim() === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  const setFontFamily = (fontFamily: string) => {
    if (!editor) return;
    if (!fontFamily) {
      editor.chain().focus().setMark('textStyle', { fontFamily: null }).removeEmptyTextStyle().run();
      return;
    }
    editor.chain().focus().setMark('textStyle', { fontFamily }).run();
  };

  const setFontSize = (fontSize: string) => {
    if (!editor) return;
    if (!fontSize) {
      editor.chain().focus().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
      return;
    }
    editor.chain().focus().setMark('textStyle', { fontSize }).run();
  };

  const setBackgroundColor = (backgroundColor: string) => {
    if (!editor) return;
    if (!backgroundColor) {
      editor.chain().focus().setMark('textStyle', { backgroundColor: null }).removeEmptyTextStyle().run();
      return;
    }
    editor.chain().focus().setMark('textStyle', { backgroundColor }).run();
  };

  const applyPreferredTextColor = () => {
    if (!editor) return;
    editor.chain().focus().setColor(preferredTextColor).run();
  };

  const applyPreferredBackgroundColor = () => {
    if (!editor) return;
    setBackgroundColor(preferredBackgroundColor);
  };

  const clearFormatting = () => {
    if (!editor) return;
    editor.chain().focus().unsetAllMarks().clearNodes().run();
  };

  const updateImageHandlePosition = () => {
    const frame = editorFrameRef.current;
    const image = activeImageRef.current;
    if (!frame || !image) {
      setImageHandle((prev) => ({ ...prev, visible: false }));
      return;
    }

    const frameRect = frame.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    setImageHandle({
      visible: true,
      left: imageRect.right - frameRect.left + frame.scrollLeft,
      top: imageRect.bottom - frameRect.top + frame.scrollTop,
    });
  };

  const clearActiveImage = () => {
    activeImageRef.current = null;
    dragWidthRef.current = null;
    setImageHandle({ visible: false, left: 0, top: 0 });
  };

  const setImageNodeSelection = (imageEl: HTMLImageElement) => {
    if (!editor) return;
    try {
      const pos = editor.view.posAtDOM(imageEl, 0);
      editor.view.dispatch(editor.view.state.tr.setSelection(NodeSelection.create(editor.view.state.doc, pos)));
    } catch {
      // noop: if DOM position can't be resolved, skip explicit node selection.
    }
  };

  const getSelectedImageWidth = () => {
    if (!editor) return null;
    const attrs = editor.getAttributes('image');
    if (!attrs || !attrs.src) return null;
    const parsed = Number.parseInt(String(attrs.width ?? ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 300;
  };

  const resizeSelectedImage = (factor: number) => {
    if (!editor || !editor.isActive('image')) return;
    const currentWidth = getSelectedImageWidth();
    if (!currentWidth) return;
    const nextWidth = Math.min(2400, Math.max(40, Math.round(currentWidth * factor)));
    editor.chain().focus().updateAttributes('image', { width: String(nextWidth), height: null }).run();
  };

  const resetSelectedImageSize = () => {
    if (!editor || !editor.isActive('image')) return;
    editor.chain().focus().updateAttributes('image', { width: null, height: null }).run();
  };

  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;

    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const imageEl = target?.closest('img') as HTMLImageElement | null;
      if (!imageEl) {
        clearActiveImage();
        return;
      }
      activeImageRef.current = imageEl;
      dragWidthRef.current = imageEl.getBoundingClientRect().width;
      setImageNodeSelection(imageEl);
      updateImageHandlePosition();
    };

    dom.addEventListener('click', handleClick);
    return () => {
      dom.removeEventListener('click', handleClick);
    };
  }, [editor]);

  useEffect(() => {
    const frame = editorFrameRef.current;
    if (!frame) return;
    const onScroll = () => updateImageHandlePosition();
    const onResize = () => updateImageHandlePosition();
    frame.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => {
      frame.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const startDragResize = (event: React.MouseEvent<HTMLDivElement>) => {
    const image = activeImageRef.current;
    if (!image || !editor) return;
    event.preventDefault();
    event.stopPropagation();
    setImageNodeSelection(image);
    dragStateRef.current = {
      startX: event.clientX,
      startWidth: image.getBoundingClientRect().width,
      dragging: true,
    };

    const onMove = (moveEvent: MouseEvent) => {
      if (!dragStateRef.current.dragging || !activeImageRef.current) return;
      const deltaX = moveEvent.clientX - dragStateRef.current.startX;
      const nextWidth = Math.min(2400, Math.max(40, Math.round(dragStateRef.current.startWidth + deltaX)));
      dragWidthRef.current = nextWidth;
      activeImageRef.current.style.width = `${nextWidth}px`;
      activeImageRef.current.style.height = 'auto';
      updateImageHandlePosition();
    };

    const onUp = () => {
      dragStateRef.current.dragging = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (!editor || !activeImageRef.current || !dragWidthRef.current) return;
      editor.chain().focus().updateAttributes('image', {
        width: String(Math.round(dragWidthRef.current)),
        height: null,
      }).run();
      activeImageRef.current.style.height = 'auto';
      updateImageHandlePosition();
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleIndent = () => {
    if (!editor) return;
    if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
      editor.chain().focus().sinkListItem('listItem').run();
      return;
    }
    editor.chain().focus().setBlockquote().run();
  };

  const handleOutdent = () => {
    if (!editor) return;
    if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
      editor.chain().focus().liftListItem('listItem').run();
      return;
    }
    if (editor.isActive('blockquote')) {
      editor.chain().focus().unsetBlockquote().run();
    }
  };

  const insertImageByUrl = () => {
    if (!editor) return;
    const src = window.prompt('Image URL');
    if (!src?.trim()) return;
    editor.chain().focus().setImage({ src: src.trim() }).run();
  };

  const resolvedBodyMaxHeight = typeof maxBodyHeight === 'number'
    ? `${maxBodyHeight}px`
    : maxBodyHeight;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        position: 'relative',
        '& .tiptap-toolbar': {
          px: 1,
          py: 0.5,
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        '& .tiptap-editor-content': {
          minHeight: `${minHeight}px`,
          maxHeight: resolvedBodyMaxHeight,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          padding: '12px 14px',
          outline: 'none',
        },
        '& .tiptap-editor-content p': {
          margin: '0 0 0.75rem',
        },
        '& .tiptap-editor-content p.is-editor-empty:first-of-type::before': {
          content: 'attr(data-placeholder)',
          color: 'text.disabled',
          pointerEvents: 'none',
          float: 'left',
          height: 0,
        },
        '& .tiptap-editor-content img': {
          maxWidth: '100%',
        },
        '& .tiptap-editor-content img:not([height])': {
          height: 'auto',
        },
        '& .tiptap-editor-content table': {
          borderCollapse: 'collapse',
          width: '100%',
        },
        '& .tiptap-editor-content th, & .tiptap-editor-content td': {
          border: '1px solid #ccc',
          padding: '6px 8px',
        },
      }}
      ref={editorFrameRef}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        className="tiptap-toolbar"
        sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <input
          ref={textColorInputRef}
          type="color"
          value={preferredTextColor}
          onChange={(event) => setPreferredTextColor(event.target.value)}
          style={{ display: 'none' }}
        />
        <input
          ref={bgColorInputRef}
          type="color"
          value={preferredBackgroundColor}
          onChange={(event) => setPreferredBackgroundColor(event.target.value)}
          style={{ display: 'none' }}
        />
        <Tooltip title={showTextTools ? 'Hide text style tools' : 'Show text style tools'}>
          <span>
            <IconButton size="small" onClick={() => setShowTextTools((v) => !v)} disabled={!editor || disabled}>
              <TextFields fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {showTextTools && (
          <>
            <select
              value={(editor?.getAttributes('textStyle').fontFamily as string) || ''}
              onChange={(event) => setFontFamily(event.target.value)}
              disabled={!editor || disabled}
              aria-label="Font family"
              style={{ height: 28, fontSize: 12, maxWidth: 130 }}
            >
              <option value="">Font</option>
              <option value="Arial, sans-serif">Arial</option>
              <option value="'Times New Roman', serif">Times New Roman</option>
              <option value="'Courier New', monospace">Courier New</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="Verdana, sans-serif">Verdana</option>
            </select>
            <select
              value={(editor?.getAttributes('textStyle').fontSize as string) || ''}
              onChange={(event) => setFontSize(event.target.value)}
              disabled={!editor || disabled}
              aria-label="Font size"
              style={{ height: 28, fontSize: 12, width: 70 }}
            >
              <option value="">Size</option>
              <option value="12px">12</option>
              <option value="14px">14</option>
              <option value="16px">16</option>
              <option value="18px">18</option>
              <option value="24px">24</option>
              <option value="32px">32</option>
            </select>
            <button
              type="button"
              onClick={applyPreferredTextColor}
              onDoubleClick={() => textColorInputRef.current?.click()}
              disabled={!editor || disabled}
              title="Click to apply text color; double-click to choose color"
              style={{ height: 28, minWidth: 28, border: '1px solid #ccc', background: '#fff', color: preferredTextColor, borderRadius: 4 }}
            >
              A
            </button>
            <button
              type="button"
              onClick={applyPreferredBackgroundColor}
              onDoubleClick={() => bgColorInputRef.current?.click()}
              disabled={!editor || disabled}
              title="Click to apply background color; double-click to choose color"
              style={{ height: 28, minWidth: 34, border: '1px solid #ccc', background: preferredBackgroundColor, color: '#222', borderRadius: 4, fontSize: 11 }}
            >
              Bg
            </button>
          </>
        )}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Bold"><span><IconButton size="small" onClick={() => editor?.chain().focus().toggleBold().run()} disabled={!editor || disabled}><FormatBold fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Italic"><span><IconButton size="small" onClick={() => editor?.chain().focus().toggleItalic().run()} disabled={!editor || disabled}><FormatItalic fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Underline"><span><IconButton size="small" onClick={() => editor?.chain().focus().toggleUnderline().run()} disabled={!editor || disabled}><FormatUnderlined fontSize="small" /></IconButton></span></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Bulleted List"><span><IconButton size="small" onClick={() => editor?.chain().focus().toggleBulletList().run()} disabled={!editor || disabled}><FormatListBulleted fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Numbered List"><span><IconButton size="small" onClick={() => editor?.chain().focus().toggleOrderedList().run()} disabled={!editor || disabled}><FormatListNumbered fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Indent"><span><IconButton size="small" onClick={handleIndent} disabled={!editor || disabled}><FormatIndentIncrease fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Outdent"><span><IconButton size="small" onClick={handleOutdent} disabled={!editor || disabled}><FormatIndentDecrease fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Block Quote"><span><IconButton size="small" onClick={() => editor?.chain().focus().toggleBlockquote().run()} disabled={!editor || disabled}><FormatQuote fontSize="small" /></IconButton></span></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Set Link"><span><IconButton size="small" onClick={setLink} disabled={!editor || disabled}><LinkIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Insert Image URL"><span><IconButton size="small" onClick={insertImageByUrl} disabled={!editor || disabled}><ImageIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Image smaller"><span><IconButton size="small" onClick={() => resizeSelectedImage(0.9)} disabled={!editor || disabled || !editor.isActive('image')}><ZoomOut fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Image larger"><span><IconButton size="small" onClick={() => resizeSelectedImage(1.1)} disabled={!editor || disabled || !editor.isActive('image')}><ZoomIn fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Reset image size"><span><IconButton size="small" onClick={resetSelectedImageSize} disabled={!editor || disabled || !editor.isActive('image')}><PhotoSizeSelectLarge fontSize="small" /></IconButton></span></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title="Remove formatting"><span><IconButton size="small" onClick={clearFormatting} disabled={!editor || disabled}><FormatClear fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Undo"><span><IconButton size="small" onClick={() => editor?.chain().focus().undo().run()} disabled={!editor || disabled || !editor?.can().undo()}><Undo fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Redo"><span><IconButton size="small" onClick={() => editor?.chain().focus().redo().run()} disabled={!editor || disabled || !editor?.can().redo()}><Redo fontSize="small" /></IconButton></span></Tooltip>
        {showTextTools && (
          <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', pt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>Text:</Box>
              {textPalette.map((color) => (
                <button
                  key={`text-${color}`}
                  type="button"
                  aria-label={`Apply text color ${color}`}
                  onClick={() => {
                    setPreferredTextColor(color);
                    editor?.chain().focus().setColor(color).run();
                  }}
                  disabled={!editor || disabled}
                  style={{ width: 18, height: 18, borderRadius: 4, border: '1px solid #cbd5e1', background: color, cursor: 'pointer' }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>Highlight:</Box>
              {highlightPalette.map((color) => (
                <button
                  key={`bg-${color}`}
                  type="button"
                  aria-label={`Apply background color ${color}`}
                  onClick={() => {
                    setPreferredBackgroundColor(color);
                    setBackgroundColor(color);
                  }}
                  disabled={!editor || disabled}
                  style={{ width: 18, height: 18, borderRadius: 4, border: '1px solid #cbd5e1', background: color, cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Stack>
      <EditorContent editor={editor} />
      {imageHandle.visible && (
        <Box
          onMouseDown={startDragResize}
          sx={{
            position: 'absolute',
            width: 14,
            height: 14,
            left: imageHandle.left - 7,
            top: imageHandle.top - 7,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            border: '2px solid #fff',
            boxShadow: 1,
            cursor: 'nwse-resize',
            zIndex: 5,
          }}
        />
      )}
    </Box>
  );
}
